import React from "react";
import { Card, Spin, Alert, QRCode } from "antd";
import {
  WhatsAppOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { GatewayStatusResponse } from "../../../Api";
import { GatewayStatusTag } from "./GatewayStatusTag";

type BotStatus = GatewayStatusResponse["status"];

interface ConnectionStatusCardProps {
  status: BotStatus;
  qrValue: string | null;
  isLoading: boolean;
}

export const ConnectionStatusCard = ({
  status,
  qrValue,
  isLoading,
}: ConnectionStatusCardProps) => {
  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Spin size="large" />
          <p className="text-sm text-gray-500 m-0">
            Connecting to WhatsApp service…
          </p>
        </div>
      );
    }

    // Ready — chatbot active
    if (status === "READY") {
      return (
        <div className="flex flex-col items-center py-10 gap-3">
          <CheckCircleOutlined className="text-5xl text-green-500" />
          <p className="text-base font-semibold text-green-600 m-0">
            Your Chatbot is Active
          </p>
          <p className="text-sm text-gray-500 text-center max-w-xs m-0">
            The AI Assistant is online and listening. Customer messages sent to
            this WhatsApp number will be processed and replied to automatically
            using Gemini AI.
          </p>
        </div>
      );
    }

    // QR code available — awaiting scan
    if (status === "QR" && qrValue) {
      return (
        <div className="flex flex-col items-center py-4 gap-4">
          <Alert
            message="Scan to Connect"
            description="Open WhatsApp → Menu / Settings → Linked Devices → Link a Device, then scan the code below."
            type="info"
            showIcon
            className="w-full"
          />
          <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
            <QRCode value={qrValue} size={240} />
          </div>
          <p className="text-xs text-gray-400 m-0">
            The code refreshes automatically every few seconds.
          </p>
        </div>
      );
    }

    // Transitional states — spinner with contextual message
    if (status === "INITIALIZING" || status === "DISCONNECTED") {
      return (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Spin size="large" />
          <p className="text-sm text-gray-500 m-0">
            {status === "DISCONNECTED"
              ? "WhatsApp disconnected — reconnecting…"
              : "Connecting to WhatsApp service…"}
          </p>
        </div>
      );
    }

    // Auth failure
    if (status === "AUTH_FAILURE") {
      return (
        <div className="flex flex-col items-center py-10 gap-3">
          <CloseCircleOutlined className="text-5xl text-red-500" />
          <p className="text-base font-semibold text-red-600 m-0">
            Authentication Failed
          </p>
          <p className="text-sm text-gray-500 text-center max-w-xs m-0">
            The session could not be restored. Restart the gateway to generate a
            new QR code.
          </p>
        </div>
      );
    }

    // Gateway unreachable / offline
    return (
      <div className="flex flex-col items-center py-10 gap-3">
        <CloseCircleOutlined className="text-5xl text-red-400" />
        <p className="text-base font-semibold text-red-600 m-0">
          Gateway Offline
        </p>
      </div>
    );
  };

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <WhatsAppOutlined className="text-green-500" />
          Connection Status
        </span>
      }
      extra={<GatewayStatusTag status={status} />}
      bordered={false}
      className="bg-white rounded-lg border border-gray-200"
    >
      {renderBody()}
    </Card>
  );
};
